import { startCase } from 'es-toolkit';
import React, { useCallback } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';

import { PropertySelectField } from '@paragon/client-tools';
import { DistanceField } from '@paragon/material';

import { changeSnappingDistance } from '../../store/actions/editorActions';
import {
  changeEditorCoordinateSystem,
  coordinateSystems,
  toggleGetObjectCoordinateSystem,
} from '../../store/slices/editorSlice';
import { useAppDispatch, useAppSelector } from '../hooks';

interface Props {
  readonly showCoordinateOptions: boolean;
  readonly onFinalize: () => void;
}

export default function TransformTool({ showCoordinateOptions, onFinalize }: Props) {
  const dispatch = useAppDispatch();

  const focusOnMount = useCallback(
    (ref: HTMLInputElement | null) =>
      void setTimeout(() => {
        ref?.focus();
        ref?.select();
      }),
    [],
  );

  const coordinateSystem = useAppSelector((state) => state.editor.coordinateSystem);
  const snappingDistance = useAppSelector((state) => state.editor.snappingDistance);
  const getObjectCoordinate = useAppSelector((state) => state.editor.getObjectCoordinate);

  const button =
    coordinateSystem === 'local' ? (
      <Button onClick={() => dispatch(toggleGetObjectCoordinateSystem())}>Get</Button>
    ) : null;

  const coordinateMessage = getObjectCoordinate ? <Typography>Click A Member or Plate Pair.</Typography> : null;

  const coordinateSelection = showCoordinateOptions && (
    <>
      <Box sx={{ m: 1 }}>
        <PropertySelectField
          property="Coordinate System"
          options={coordinateSystems}
          value={coordinateSystem}
          submit={(coordinateSystem) => dispatch(changeEditorCoordinateSystem(coordinateSystem))}
          getName={startCase}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>{button}</Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>{coordinateMessage}</Box>
      </Box>
      <Divider />
    </>
  );

  return (
    <Box sx={{ p: 1 }}>
      {coordinateSelection}
      <Box sx={{ m: 1 }}>
        <DistanceField
          inputRef={focusOnMount}
          label="Distance"
          onSubmit={(value, { finalizing }) => {
            dispatch(changeSnappingDistance(value));
            if (finalizing) onFinalize();
          }}
          value={snappingDistance}
          validator={(value) => {
            if (value <= 0) {
              return `must be greater than zero`;
            }
          }}
        />
      </Box>
    </Box>
  );
}
